import { useState, useEffect, useCallback } from 'react'
import { M365User, UpdateUserRequest, M365DirectoryRole, M365UserGroupsResponse } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface ProfileSectionProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onUserUpdated: (user: M365User) => void
}

export function ProfileSection({
  user,
  customerId,
  customerTenantId,
  onUserUpdated,
}: ProfileSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Directory roles state
  const [directoryRoles, setDirectoryRoles] = useState<M365DirectoryRole[]>([])
  const [rolesLoading, setRolesLoading] = useState(true)

  // Fetch directory roles
  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true)
      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365UserGroups(token, customerId, user.id) as M365UserGroupsResponse
      setDirectoryRoles(response.directoryRoles || [])
    } catch {
      // Silently fail - roles are optional info
      setDirectoryRoles([])
    } finally {
      setRolesLoading(false)
    }
  }, [customerId, customerTenantId, user.id, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  // Form state
  const [formData, setFormData] = useState<UpdateUserRequest>({
    givenName: user.givenName || '',
    surname: user.surname || '',
    displayName: user.displayName || '',
    jobTitle: user.jobTitle || '',
    department: user.department || '',
    officeLocation: user.officeLocation || '',
    mobilePhone: user.mobilePhone || '',
    usageLocation: user.usageLocation || '',
  })

  const handleInputChange = (field: keyof UpdateUserRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)

      // Helper to normalize values: empty strings become undefined (omitted from JSON)
      const normalizeValue = (value: string | undefined): string | undefined => {
        return value && value.trim() !== '' ? value.trim() : undefined
      }

      // Helper to check if value changed (comparing normalized values)
      const hasChanged = (formValue: string | undefined, userValue: string | undefined): boolean => {
        const normalizedForm = normalizeValue(formValue) || ''
        const normalizedUser = normalizeValue(userValue) || ''
        return normalizedForm !== normalizedUser
      }

      // Only send changed fields, converting empty strings to null
      const updates: UpdateUserRequest = {}
      if (hasChanged(formData.givenName, user.givenName)) updates.givenName = normalizeValue(formData.givenName)
      if (hasChanged(formData.surname, user.surname)) updates.surname = normalizeValue(formData.surname)
      if (hasChanged(formData.displayName, user.displayName)) updates.displayName = normalizeValue(formData.displayName)
      if (hasChanged(formData.jobTitle, user.jobTitle)) updates.jobTitle = normalizeValue(formData.jobTitle)
      if (hasChanged(formData.department, user.department)) updates.department = normalizeValue(formData.department)
      if (hasChanged(formData.officeLocation, user.officeLocation)) updates.officeLocation = normalizeValue(formData.officeLocation)
      if (hasChanged(formData.mobilePhone, user.mobilePhone)) updates.mobilePhone = normalizeValue(formData.mobilePhone)
      if (hasChanged(formData.usageLocation, user.usageLocation)) updates.usageLocation = normalizeValue(formData.usageLocation)

      if (Object.keys(updates).length === 0) {
        setIsEditing(false)
        return
      }

      const response = await api.updateM365User(token, customerId, user.id, updates) as { user: M365User }
      onUserUpdated(response.user)
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      givenName: user.givenName || '',
      surname: user.surname || '',
      displayName: user.displayName || '',
      jobTitle: user.jobTitle || '',
      department: user.department || '',
      officeLocation: user.officeLocation || '',
      mobilePhone: user.mobilePhone || '',
      usageLocation: user.usageLocation || '',
    })
    setIsEditing(false)
    setError(null)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Profile Information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          {/* First Name */}
          <div>
            <dt className="text-sm font-medium text-gray-500">First Name</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.givenName}
                onChange={(e) => handleInputChange('givenName', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.givenName || 'N/A'}</dd>
            )}
          </div>

          {/* Last Name */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Last Name</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.surname}
                onChange={(e) => handleInputChange('surname', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.surname || 'N/A'}</dd>
            )}
          </div>

          {/* Display Name */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Display Name</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.displayName}</dd>
            )}
          </div>

          {/* Email */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">{user.mail || user.userPrincipalName}</dd>
          </div>

          {/* Job Title */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Job Title</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.jobTitle || 'N/A'}</dd>
            )}
          </div>

          {/* Department */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Department</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.department || 'N/A'}</dd>
            )}
          </div>

          {/* Office */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Office</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.officeLocation}
                onChange={(e) => handleInputChange('officeLocation', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.officeLocation || 'N/A'}</dd>
            )}
          </div>

          {/* Mobile Phone */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Mobile Phone</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.mobilePhone}
                onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.mobilePhone || 'N/A'}</dd>
            )}
          </div>

          {/* Usage Location */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Usage Location</dt>
            {isEditing ? (
              <input
                type="text"
                value={formData.usageLocation}
                onChange={(e) => handleInputChange('usageLocation', e.target.value)}
                placeholder="e.g., US"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm py-2.5 px-3"
              />
            ) : (
              <dd className="mt-1 text-sm text-gray-900">{user.usageLocation || 'N/A'}</dd>
            )}
          </div>

          {/* Created Date (read-only) */}
          <div>
            <dt className="text-sm font-medium text-gray-500">Account Created</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(user.createdDateTime)}</dd>
          </div>

          {/* Last Sign-in (read-only) */}
          <div className="sm:col-span-2">
            <dt className="text-sm font-medium text-gray-500">Last Sign-in</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(user.lastSignInDateTime)}</dd>
          </div>
        </dl>

        {/* Directory Roles Section */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Directory Roles
          </h4>
          {rolesLoading ? (
            <div className="flex items-center text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              Loading roles...
            </div>
          ) : directoryRoles.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {directoryRoles.map(role => (
                <span
                  key={role.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  title={role.description}
                >
                  <svg className="h-3.5 w-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  {role.displayName}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">No directory roles assigned</p>
          )}
        </div>

        {/* Email Aliases Section */}
        {user.proxyAddresses && user.proxyAddresses.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <svg className="h-4 w-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Addresses
            </h4>
            <div className="space-y-2">
              {/* Primary email (SMTP: prefix - uppercase) */}
              {user.proxyAddresses
                .filter(addr => addr.startsWith('SMTP:'))
                .map(addr => (
                  <div key={addr} className="flex items-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2">
                      Primary
                    </span>
                    <span className="text-sm text-gray-900">{addr.substring(5)}</span>
                  </div>
                ))}
              {/* Aliases (smtp: prefix - lowercase) */}
              {user.proxyAddresses
                .filter(addr => addr.startsWith('smtp:'))
                .map(addr => (
                  <div key={addr} className="flex items-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mr-2">
                      Alias
                    </span>
                    <span className="text-sm text-gray-900">{addr.substring(5)}</span>
                  </div>
                ))}
              {/* Other addresses (SIP:, X500:, etc.) - show collapsed */}
              {user.proxyAddresses.filter(addr => !addr.toLowerCase().startsWith('smtp:')).length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                    Show other addresses ({user.proxyAddresses.filter(addr => !addr.toLowerCase().startsWith('smtp:')).length})
                  </summary>
                  <div className="mt-2 pl-4 space-y-1">
                    {user.proxyAddresses
                      .filter(addr => !addr.toLowerCase().startsWith('smtp:'))
                      .map(addr => {
                        const [prefix, ...rest] = addr.split(':')
                        return (
                          <div key={addr} className="flex items-center text-xs text-gray-500">
                            <span className="font-mono mr-2">{prefix}:</span>
                            <span className="truncate">{rest.join(':')}</span>
                          </div>
                        )
                      })}
                  </div>
                </details>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
